<template>
  <header class="page-header">
    <div class="w-100 d-flex flex-column gap-2">
      <nav class="d-flex align-items-center gap-3 fs-14" aria-label="breadcrumb">
        <span class="d-flex align-items-center gap-1">
          <i class="bx bxs-chevron-left" />
          <GuButton variant="link" class="p-0 link-dark link-offset-2" @click="$router.back()">Back</GuButton>
        </span>
      </nav>

      <div class="d-flex flex-row align-items-center">
        <h1 v-if="companyStore.getCompanyName">{{ companyStore.getCompanyName }} Members</h1>

        <div class="page-button-group ms-auto">
          <GuDialog v-model:open="isOpen">
            <GuDialogTrigger as-child>
              <GuButton icon="bx-group" variant="secondary">
                <span class="d-none d-sm-inline">Manage Email Groups</span>
              </GuButton>
            </GuDialogTrigger>

            <GuDialogContent v-if="selectedEmailGroup" centered>
              <template #title>
                <h5 class="modal-title fs-18">Edit Email Group</h5> 
              </template>

              <ManageEmailGroupMembers :email-group="selectedEmailGroup" />

              <template #footer>
                <GuButton variant="secondary" @click="clearSelectedEmailGroup">Back</GuButton>
              </template>
            </GuDialogContent>

            <GuDialogContent v-else centered>
              <template #title>
                <h5 class="modal-title fs-18">Manage Email Groups</h5>
              </template>

              <ManageEmailGroups @email-group-selected="emailGroupSelected" />

              <template #footer>
                <GuDialogClose as-child>
                  <GuButton variant="secondary">Close</GuButton>
                </GuDialogClose>
              </template>
            </GuDialogContent>
          </GuDialog>

          <GuLink
            variant="primary"
            :href="`/CompanyManagement/add-company-member/${companyId}`"
            icon="bx-envelope"
          >
            <span class="d-none d-sm-inline">Invite User</span>
          </GuLink>

          <GuLink
            variant="primary"
            :href="`/CompanyManagement/company/${companyId}/edit`"
            icon="bx-edit"
          >
            <span class="d-none d-sm-inline">Edit Company</span>
          </GuLink>
        </div>
      </div>
    </div>
  </header>

  <GuDatagridServer
    :config="config"
    :site-config-name="siteConfigName"
    class="mx-5"
    @grid-ready="onGridReady"
  />
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'

import { GuDatagridServer } from '@gu-datagrid-new'
import { GuButton } from '@shared/gu-components/button'
import { GuLink } from '@shared/gu-components/link'
import {
  GuDialog,
  GuDialogClose,
  GuDialogContent,
  GuDialogTrigger,
} from '@shared/gu-components/dialog'

import { COMPANIES_API, getCompany } from '@companies/api/companies.api'
import { useCompanyStore } from '@companies/stores/CompanyStore'
import { COMPANIES_MEMBERS_CONFIG } from '@shared/constants/siteContent'
import { urlSubst } from '@shared/utils/url'

import ManageEmailGroups from '@companies/components/ManageEmailGroups.vue'
import ManageEmailGroupMembers from '@companies/components/ManageEmailGroupMembers.vue'

import { gridConfig } from '@companies/config/company.members.datagrid.config'

const props = defineProps({
  companyId: {
    type: [String, Number],
    required: true,
  },
})

const companyStore = useCompanyStore()

const config = {
  ...gridConfig,
  rowsUrl: urlSubst(COMPANIES_API.GET_COMPANY_MEMBERS, { companyId: props.companyId }),
}

const siteConfigName = COMPANIES_MEMBERS_CONFIG

const { data: companyData } = getCompany(props.companyId)

const gridApi = ref(null)

const selectedEmailGroup = ref(null)
const isOpen = ref(false)

watch(companyData, (data) => {
  if (data) companyStore.setCompany(data)
}, { immediate: true })

watch([companyData, gridApi], ([data, api]) => {
  if (data && api) {
    const currentColumnDefs = api.getColumnDefs()

    const updatedColumnDefs = currentColumnDefs.map((colDef) => {
      if (colDef.headerName === 'Actions' || colDef.headerName === 'Application Access') {
        return {
          ...colDef,
          cellRendererParams: {
            ...colDef.cellRendererParams,
            companyName: data.name,
          },
        }
      }
      return colDef
    })

    api.setGridOption('columnDefs', updatedColumnDefs)
    api.refreshCells({ columns: ['status', 'applicationAssociation'], force: true })
  }
}, { immediate: true })

onBeforeUnmount(() => companyStore.resetCompany())

function onGridReady (evt) {
  gridApi.value = evt.api
}

function emailGroupSelected (emailGroup) {
  selectedEmailGroup.value = emailGroup
}

function clearSelectedEmailGroup () {
  selectedEmailGroup.value = null
}
</script>

<style>
@media (min-width: 576px) {
  .modal-dialog {
    width: 90vw;
    max-width: 750px;
  }
}
</style>
